import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, KeyRound } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { PushDeviceList } from '../components/settings/PushDeviceList';
import { PushToggle } from '../components/settings/PushToggle';
import { SessionList } from '../components/settings/SessionList';

/**
 * Alerts and signed-in devices, on one screen (D39's unbuilt settings route, and D41's
 * session list).
 *
 * One screen rather than two because a person looking at "the phones that can reach my
 * account" does not distinguish "receives alerts" from "has a live session". Building
 * them separately would have produced two lists of the same handsets, from two features,
 * neither of which is the answer somebody came here for.
 *
 * The first settings route the client has ever had, and deliberately minimal: no
 * preferences, no profile editing. Everything here answers one question.
 */
export function NotificationSettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Bumped whenever the toggle changes this device's subscription, so the device list
  // below re-reads and the row for this phone appears or disappears with the switch.
  const [devicesVersion, setDevicesVersion] = useState(0);

  return (
    <div className="flex flex-col">
      <header className="flex items-center gap-2 border-b border-slate-800 px-2 py-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label={t('settings.back')}
          className="flex min-h-[48px] items-center gap-1 rounded-lg bg-transparent px-2 text-sm text-slate-300"
        >
          <ChevronLeft className="size-5" aria-hidden="true" />
          {t('settings.back')}
        </button>
      </header>

      <div className="px-4 py-3">
        <h1 className="text-base font-semibold text-slate-100">{t('settings.heading')}</h1>
        <p className="mt-1 text-xs text-slate-400">{user?.email}</p>
      </div>

      <Section title={t('settings.alerts.heading')} hint={t('settings.alerts.hint')}>
        {/* The control, not the prompt. `PushEnableBanner` (still in the drawer) offers
            "turn on" only while a device is unsubscribed; this switch is present in every
            state — on, off, never asked, blocked, install-first, unsupported — because a
            settings screen that can only turn a thing on is not a setting. Per device,
            and labelled so: the list beneath is where somebody turns off a phone they no
            longer carry. */}
        <PushToggle onChanged={() => setDevicesVersion((version) => version + 1)} />
        <PushDeviceList key={devicesVersion} />
      </Section>

      <Section title={t('settings.sessions.heading')} hint={t('settings.sessions.hint')}>
        <SessionList />
      </Section>

      {/*
        A link rather than the form itself. This screen answers "what can reach my account
        and what will it tell me"; a credential form is a different question, and the page
        is deliberately one question deep. It sits directly under the session list because
        that is where somebody who has just seen an unfamiliar device will look next.
      */}
      <Section title={t('security.heading')} hint={t('security.explain')}>
        <Link
          to="/settings/security"
          className="inline-flex min-h-[48px] items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-4 text-sm text-slate-100 no-underline"
        >
          <KeyRound className="size-4 shrink-0" aria-hidden="true" />
          {t('security.save')}
        </Link>
      </Section>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-2 border-t border-slate-800">
      <div className="px-4 pb-1 pt-4">
        <h2 className="text-sm font-semibold text-slate-100">{title}</h2>
        <p className="mt-1 text-xs text-slate-400">{hint}</p>
      </div>
      {children}
    </section>
  );
}
